import React, {useState, useEffect, useRef} from 'react'
import {
  doc,
  collection,
  onSnapshot,
  getDocs,
  query,
  where,
  addDoc,
  serverTimestamp,
  deleteDoc,
  updateDoc,
} from 'firebase/firestore'
import db from 'services/firebase'
import {useAuth} from 'app/modules/auth'
import Drawer from 'react-modern-drawer'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import DrawerHeader from '../drawer-components/DrawerHeader'
import {DrawerContent} from '../drawer-components/DrawerContent'
import InfoCard from '../drawer-components/InfoCard'
import DrawerFooter from '../drawer-components/DrawerFooter'
import {AddDrawerIntegrationProps} from '../utils/types'
import { useNavigate } from 'react-router-dom'

const ClickUpDrawer: React.FC<AddDrawerIntegrationProps> = ({id, isOpen, onClose}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [openAccordion, setOpenAccordion] = useState(false)
  const [openAccordion2, setOpenAccordion2] = useState(false)
  const {currentUser} = useAuth()
  const [showAlert, setShowAlert] = useState(false)
  const drawerTopRef = useRef<HTMLDivElement>(null)
  const [integration, setIntegration] = useState<any[]>([])
  const isConnected =
    integration.length > 0 &&
    integration[0]?.status === 'Connected' &&
    integration[0]?.name === 'ClickUp'
  const [accessToken, setAccessToken] = useState<string | null>(
    localStorage.getItem('clickup_access_token')
  )
  const [projectSelecting, setProjectSelecting] = useState(false)
  const [lists, setLists] = useState('')
  const [connected, setConnected] = useState(false)
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [isListLocked, setIsListLocked] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    if (isOpen && id) {
      getIntegration()
      navigate('/integrations', {replace: true})
    }
  }, [id, isOpen])

  useEffect(() => {
    if (showAlert && drawerTopRef.current) {
      drawerTopRef.current.scrollIntoView({behavior: 'smooth'})
    }
  }, [showAlert])

  useEffect(() => {
    if (
      isOpen &&
      connected &&
      !isListLocked &&
      integration[0]?.lists?.[integration[0]?.team_id]?.length > 0 &&
      !(lists || integration[0]?.list_id)
    ) {
      setFormErrors((prev) => ({ ...prev, list: 'Please select a project/list' }))
    } else {
      setFormErrors((prev) => ({ ...prev, list: '' }))
    }
  }, [isOpen, connected, isListLocked, integration, lists])

  const getIntegration = () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      if (!id) return
      const integrationRef = doc(db, 'tenants', tenantId, 'integrations', id)
      const unsubscribe = onSnapshot(integrationRef, (doc) => {
        if (doc.exists()) {
          const data = doc.data()
          if (data.name === 'ClickUp') {
            const integrationData = {
              ...data,
              id: doc.id,
              name: data.name,
              accessToken: data.accessToken,
              status: data.status,
            }
            setIntegration([integrationData])
            setAccessToken(data.accessToken)
            setConnected(data.status === 'Connected')
            setIsListLocked(data.listLocked || false)
          }
        }
      })
      return unsubscribe
    } catch (error) {
      console.error('Error getting integration:', error)
    }
  }

  const handleConnect = async () => {
    if (!accessToken) {
      localStorage.setItem('pendingIntegration', 'ClickUp')
      const clientId = process.env.REACT_APP_CLICKUP_CLIENT_ID
      const redirectUri = process.env.REACT_APP_CLICKUP_REDIRECT_URI || ''
      const authUrl = `https://app.clickup.com/api?client_id=${clientId}&redirect_uri=${encodeURIComponent(
        redirectUri
      )}`
      window.location.href = authUrl
      return
    }
  }

  const handleDisconnect = async () => {
    try {
      setIsLoading(true)
      const tenantId = currentUser?.tenantId || ''
      if (!id || !tenantId) return

      const tenantRef = doc(db, 'tenants', tenantId)
      const integrationRef = doc(db, 'tenants', tenantId, 'integrations', id)
      await deleteDoc(integrationRef)

      const tasksRef = collection(tenantRef, 'tasklists')
      const clickUpTasksQuery = query(tasksRef, where('source', '==', 'ClickUp'))
      const clickUpTasksSnapshot = await getDocs(clickUpTasksQuery)

      const deleteTaskPromises = clickUpTasksSnapshot.docs.map(async (taskDoc) => {
        await deleteDoc(doc(db, 'tenants', tenantId, 'tasklists', taskDoc.id))
      })

      const vendorsRef = collection(tenantRef, 'vendors')
      const clickUpVendorQuery = query(vendorsRef, where('vendor_name', '==', 'ClickUp'))
      const clickUpVendorSnapshot = await getDocs(clickUpVendorQuery)
      const accessReviewRef = doc(db, 'tenants', tenantId, 'access_review', 'ClickUp')
      await deleteDoc(accessReviewRef)

      const deleteVendorPromises = clickUpVendorSnapshot.docs.map(async (vendorDoc) => {
        await deleteDoc(doc(db, 'tenants', tenantId, 'vendors', vendorDoc.id))
      })

      await Promise.all([...deleteTaskPromises, ...deleteVendorPromises])

      const activitiesRef = collection(tenantRef, 'activities')
      const currentDate = new Date()
      const todaysDate = `${
        currentDate.getMonth() + 1
      }/${currentDate.getDate()}/${currentDate.getFullYear()}`
      await addDoc(activitiesRef, {
        message: `${currentUser?.userName} disconnected ClickUp integration`,
        timestamp: serverTimestamp(),
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        action: 'Integration disconnected',
        target: 'Integration Management',
        email: currentUser?.email,
        username: currentUser?.userName,
        date: todaysDate,
      })

      showSuccessToast({message: 'ClickUp integration disconnected'})

      localStorage.removeItem('clickup_access_token')
      setIntegration([])
      handleClose()
    } catch (error) {
      showErrorToast({message: 'Failed to disconnect ClickUp'})
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setShowAlert(false)
    onClose()
  }

  const validateForm = () => {
    const errors: Record<string, string> = {}

    if (!lists.trim()) {
      errors.lists = 'Team Name is required'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleList = async (list) => {
    setProjectSelecting(true)
    if (!id && !validateForm()) {
      setProjectSelecting(false)
      return
    }

    if (currentUser) {
      if (id) {
        const tenantId = currentUser.tenantId || ''
        const docRef = doc(db, 'tenants', tenantId, 'integrations', id || '')

        await updateDoc(docRef, {list_id: list, listLocked: true})
      }

      showSuccessToast({message: 'ClickUp list updated successfully'})
      window.scrollTo(0, 0)
    }
    setProjectSelecting(false)
  }

  return (
    <>
      <Drawer open={isOpen} onClose={handleClose} direction='right' className='' size='45%'>
        <DrawerHeader title='ClickUp' onClose={onClose} />

        <div
          className='scroll-y overflow-auto p-6 mb-10'
          tabIndex={-1}
          style={{
            height: 'calc(100vh - 120px)',
            paddingBottom: '80px',
          }}
        >
          <div className='form'>
            <DrawerContent
              title='ClickUp'
              description='Task Management and Productivity Platform'
              logo='/media/integrations/clickup.svg'
              integration={integration}
              openAccordion={openAccordion}
              setOpenAccordion={setOpenAccordion}
              openAccordion2={openAccordion2}
              setOpenAccordion2={setOpenAccordion2}
              permissionInfo='SecureSlate will be granted permissions to read and write ClickUp tasks via OAuth 2.0,
              respecting user permissions.'
              accessTypeinfo='Requires an active ClickUp integration with sufficient permissions to access tasks.'
            />
            <div className='mb-4 d-flex justify-content-between align-items-center'>
              <div className='fs-6 fw-bold text-bold'>
                {isConnected
                  ? 'ClickUp Connected to SecureSlate'
                  : 'Connect ClickUp to Enable Task Management'}
              </div>
              {projectSelecting && (
              <span className='badge px-3 rounded-pill ms-4' style={{border: '1px solid #E1E1E1'}}>
                <span className='fs-7'>Updating...</span>
              </span>
            )}
            </div>
            <>
              <div className='mb-8'>
                <label className='fw-semibold mb-2 fs-6'>Select a Project</label>
                <select
                  className={`form-select ${formErrors.list ? 'is-invalid' : ''}`}
                  value={lists || integration[0]?.list_id || ''}
                  onChange={(e) => {
                    setLists(e.target.value)
                    if (formErrors.list && e.target.value.trim() !== '') {
                      const updatedErrors = {...formErrors}
                      delete updatedErrors.list
                      setFormErrors(updatedErrors)
                    }
                    handleList(e.target.value)
                  }}
                  disabled={!connected || isListLocked}
                  onBlur={() => {
                    if (!isListLocked && (!lists && !integration[0]?.list_id)) {
                      setFormErrors((prev) => ({ ...prev, list: 'Please select a project/list' }))
                    }
                  }}
                >
                  <option value=''>Select List</option>
                  {integration[0]?.lists?.[integration[0]?.team_id]?.map(
                    (list: any) =>
                      list.name !== 'Personal List' && (
                        <option key={list.id} value={list.id}>
                          {list.name} ({list.space.name}
                          {!list.folder.hidden && ` - ${list.folder.name}`})
                        </option>
                      )
                  )}
                </select>
                {isListLocked && (
                  <small className='text-muted'>
                    List selection is locked. To change, reconnect the integration.
                  </small>
                )}
                {formErrors.list && <span className='text-danger'>{formErrors.list}</span>}
              </div>
            </>
            {!isConnected &&
            <InfoCard
              type='info'
              title='How to enable ClickUp integration?'
              description='Connect your ClickUp account via OAuth 2.0. Click Scan Tasks to fetch tasks or create new ones. Results are stored and displayed in SecureSlate.'
            />
            }
          </div>
        </div>
        <DrawerFooter
          isConnected={isConnected}
          isLoading={isLoading}
          onConnect={handleConnect}
          onDisconnect={handleDisconnect}
          customActions={!isConnected}
        />
      </Drawer>
    </>
  )
}

export default ClickUpDrawer
